import { useEffect, useState, useCallback } from 'react';
import {
  getCurrentSubscription,
  upgradeSubscription,
  downgradeSubscription,
  cancelSubscription,
  Subscription,
  UpgradeRequest,
  DowngradeRequest
} from '../api/subscriptions';

interface SubscriptionState {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook pour gérer la subscription de l'utilisateur
 *
 * Charge automatiquement la subscription actuelle et fournit des méthodes
 * pour upgrade, downgrade et annulation
 */
export function useSubscription() {
  const [state, setState] = useState<SubscriptionState>({
    subscription: null,
    loading: true,
    error: null
  });

  const loadSubscription = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const subscription = await getCurrentSubscription();
      setState({
        subscription,
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Failed to load subscription:', error);
      setState({
        subscription: null,
        loading: false,
        error: error.response?.data?.message || error.message || 'Failed to load subscription'
      });
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  /**
   * Upgrade vers un plan supérieur
   */
  const upgrade = useCallback(async (data: UpgradeRequest): Promise<boolean> => {
    try {
      const updatedSubscription = await upgradeSubscription(data);
      setState((prev) => ({
        ...prev,
        subscription: updatedSubscription,
        error: null
      }));
      return true;
    } catch (error: any) {
      console.error('Failed to upgrade subscription:', error);
      setState((prev) => ({
        ...prev,
        error: error.response?.data?.message || error.message || 'Failed to upgrade subscription'
      }));
      return false;
    }
  }, []);

  /**
   * Downgrade vers un plan inférieur
   */
  const downgrade = useCallback(async (data: DowngradeRequest): Promise<boolean> => {
    try {
      const updatedSubscription = await downgradeSubscription(data);
      setState((prev) => ({
        ...prev,
        subscription: updatedSubscription,
        error: null
      }));
      return true;
    } catch (error: any) {
      console.error('Failed to downgrade subscription:', error);
      setState((prev) => ({
        ...prev,
        error: error.response?.data?.message || error.message || 'Failed to downgrade subscription'
      }));
      return false;
    }
  }, []);

  /**
   * Annuler la subscription (retour au plan FREE)
   */
  const cancel = useCallback(async (): Promise<boolean> => {
    try {
      const updatedSubscription = await cancelSubscription();
      setState((prev) => ({
        ...prev,
        subscription: updatedSubscription,
        error: null
      }));
      return true;
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      setState((prev) => ({
        ...prev,
        error: error.response?.data?.message || error.message || 'Failed to cancel subscription'
      }));
      return false;
    }
  }, []);

  /**
   * Rafraîchir manuellement la subscription
   */
  const refresh = useCallback(() => {
    loadSubscription();
  }, [loadSubscription]);

  return {
    subscription: state.subscription,
    loading: state.loading,
    error: state.error,
    upgrade,
    downgrade,
    cancel,
    refresh
  };
}

/**
 * Helper pour obtenir le nom lisible du plan
 */
export function getPlanName(plan: string): string {
  const names: Record<string, string> = {
    free: 'Free',
    standard: 'Standard',
    entreprise: 'Entreprise',
    business: 'Business'
  };
  return names[plan] || plan;
}

/**
 * Helper pour obtenir le badge de statut
 */
export function getStatusBadge(status: string): { label: string; color: string } {
  const badges: Record<string, { label: string; color: string }> = {
    active: { label: 'Actif', color: 'green' },
    OVER_QUOTA: { label: 'Actif', color: 'green' }, // OVER_QUOTA est déjà indiqué par la bannière
    suspended: { label: 'Suspendu', color: 'orange' },
    cancelled: { label: 'Annulé', color: 'red' },
    trial_expired: { label: 'Trial expiré', color: 'red' }
  };
  return badges[status] || { label: 'Actif', color: 'green' };
}
